import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Users, UserPlus, ClipboardCheck, ClipboardList, Search, Loader2, ArrowLeft, FileText } from 'lucide-react';
import EmployeeProfileCard from '@/components/personnel/EmployeeProfileCard';
import { PersonnelDirectoryCard } from '@/components/personnel/PersonnelDirectoryCard';
import PersonnelIntake from '@/components/personnel/PersonnelIntake';
import { GovFormsPanel } from '@/components/personnel/GovFormsPanel';
import { isGlobalAdmin } from '@/lib/workspaces';
import SidebarShell from '@/components/wom/SidebarShell';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

const NewHire = ({ onCreated }) => {
  const { token } = useContext(AuthContext);
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({ worker_classification: 'W-2 Employee', primary_operational_role: '', payroll_frequency: 'Semi-monthly' });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(null);
  useEffect(() => { axios.get(`${API}/personnel/onboarding/meta`, auth(token)).then((r) => setMeta(r.data)).catch(() => {}); }, [token]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setErr(null);
    if (!f.legal_first_name || !f.legal_last_name) { setErr('Legal first and last name are required.'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/personnel`, { ...f, onboarding_status: 'In Progress' }, auth(token));
      onCreated(res.data.id || res.data?.personnel?.id);
    } catch (e) { setErr(e.response?.data?.detail || 'Could not create the application'); }
    setBusy(false);
  };
  const In = ({ k, label, type = 'text', req }) => (
    <label className="block"><span className="text-[11px] font-semibold text-slate-500">{label}{req && ' *'}</span>
      <input type={type} value={f[k] || ''} onChange={set(k)} className="mt-0.5 w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm" data-testid={`nh-${k}`} /></label>
  );
  const group = 'rounded-xl border border-slate-200 bg-white p-4';
  const title = 'mb-3 text-sm font-bold text-[#0E2A47]';
  return (
    <div className="space-y-4" data-testid="new-hire-form">
      <div className="text-xs text-slate-500">Add Personnel application — reuses the Personnel Master. A permanent five-digit personnel number is assigned only upon Global Administrator approval; drafts and pending applications never appear as active personnel.</div>
      <div className={group}><div className={title}>A. Personal Information</div>
        <div className="grid gap-3 sm:grid-cols-3">
          {In({ k: 'legal_first_name', label: 'Legal first name', req: true })}{In({ k: 'legal_middle_name', label: 'Middle' })}{In({ k: 'legal_last_name', label: 'Legal last name', req: true })}
          {In({ k: 'phone', label: 'Telephone' })}{In({ k: 'email', label: 'Email' })}{In({ k: 'date_of_birth', label: 'Date of birth', type: 'date' })}
          {In({ k: 'proposed_start_date', label: 'Proposed start date', type: 'date' })}
          <label className="block"><span className="text-[11px] font-semibold text-slate-500">Position sought</span>
            <select value={f.position || ''} onChange={set('position')} className="mt-0.5 w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm" data-testid="nh-position">
              <option value="">Select…</option>{(meta?.positions || []).map((p) => <option key={p}>{p}</option>)}</select></label>
          {In({ k: 'street_address', label: 'Street address' })}{In({ k: 'city', label: 'City' })}{In({ k: 'state', label: 'State' })}
          {In({ k: 'zip_code', label: 'ZIP' })}{In({ k: 'emergency_contact_name', label: 'Emergency contact' })}{In({ k: 'emergency_contact_phone', label: 'Emergency phone' })}
        </div>
      </div>
      <div className={group}><div className={title}>B. Worker Classification</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block"><span className="text-[11px] font-semibold text-slate-500">Worker classification *</span>
            <select value={f.worker_classification} onChange={set('worker_classification')} className="mt-0.5 w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm" data-testid="nh-classification">
              {(meta?.worker_classifications || []).map((c) => <option key={c}>{c}</option>)}</select></label>
          <label className="block"><span className="text-[11px] font-semibold text-slate-500">Operational role</span>
            <select value={f.primary_operational_role} onChange={set('primary_operational_role')} className="mt-0.5 w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm" data-testid="nh-role">
              <option value="">Select…</option>{(meta?.operational_roles || []).map((r) => <option key={r}>{r}</option>)}</select></label>
        </div>
        {f.worker_classification === 'Registered Business Subcontractor' && <div className="mt-3 grid gap-3 sm:grid-cols-2">{In({ k: 'company_name', label: 'Legal business name' })}{In({ k: 'ein', label: 'FEIN / EIN' })}</div>}
        <div className="mt-2 text-[11px] text-slate-400">Classification-specific documents (W-4/I-9 for W-2, W-9 for 1099/business, insurance/Sunbiz for business) are collected on the Employee Profile Card after this record is created.</div>
      </div>
      {err && <div className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-600" data-testid="nh-err">{err}</div>}
      <button onClick={submit} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-[#0E2A47] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="nh-create">{busy ? <Loader2 className="animate-spin" size={16} /> : <UserPlus size={16} />}Create application & open profile</button>
    </div>
  );
};

const Approvals = () => {
  const { token } = useContext(AuthContext);
  const [rows, setRows] = useState(null); const [open, setOpen] = useState(null); const [comments, setComments] = useState(''); const [deciding, setDeciding] = useState(false);
  const load = () => axios.get(`${API}/personnel/approvals/queue`, auth(token)).then((r) => setRows(r.data.personnel || [])).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const decide = async (id, decision) => {
    setDeciding(true);
    try {
      const r = await axios.post(`${API}/personnel/${id}/approval`, { decision, comments }, auth(token));
      const num = r.data?.personnel_number;
      toast.success(decision === 'approve' ? `Approved — personnel #${num} assigned` : `Application ${decision === 'return' ? 'returned for correction' : 'rejected'}`);
      setComments(''); setOpen(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record the decision'); }
    setDeciding(false);
  };
  if (open) return (
    <div data-testid="approval-detail">
      <button onClick={() => setOpen(null)} className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-[#1763B6]"><ArrowLeft size={14} />Back to queue</button>
      <EmployeeProfileCard personnelId={open} onHiringDecision={() => { setOpen(null); load(); }} />
    </div>
  );
  if (!rows) return <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>;
  return (
    <div data-testid="approvals-queue">
      {rows.length === 0 ? <p className="py-10 text-center text-sm text-slate-400">No candidates pending approval.</p>
        : <div className="space-y-2">{rows.map((p) => (
          <button key={p.id} onClick={() => setOpen(p.id)} className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left hover:border-[#1763B6]" data-testid={`approval-row-${p.id}`}>
            <span><span className="font-semibold text-[#0E2A47]">{p.name}</span> <span className="text-xs text-slate-400">{p.personnel_number ? `#${p.personnel_number}` : (p.application_id || 'Application')} · {p.worker_classification}</span>
              {p.onboarding && <span className="ml-2 text-[11px] font-semibold text-slate-500" data-testid={`approval-onboarding-${p.id}`}>· Onboarding {p.onboarding.complete}/{p.onboarding.total}{p.onboarding.complete < p.onboarding.total ? ` · ${p.onboarding.total - p.onboarding.complete} outstanding` : ' ✓'}</span>}</span>
            <span className="text-xs font-semibold text-amber-700">Pending Approval →</span></button>))}</div>}
    </div>
  );
};

const Directory = () => {
  const { token } = useContext(AuthContext);
  const [list, setList] = useState(null); const [q, setQ] = useState(''); const [sel, setSel] = useState(null);
  useEffect(() => { axios.get(`${API}/personnel?status_filter=all`, auth(token)).then((r) => setList(Array.isArray(r.data) ? r.data : (r.data.personnel || r.data.items || []))).catch(() => setList([])); }, [token]);
  if (sel) return <EmployeeProfileCard personnelId={sel} onClose={() => setSel(null)} />;
  const rows = (list || []).filter((p) => !q || (p.name || '').toLowerCase().includes(q.toLowerCase()) || String(p.personnel_number).includes(q));
  return (
    <div data-testid="personnel-directory">
      <div className="mb-3 flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2"><Search size={15} className="text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or personnel #" className="w-full text-sm outline-none" data-testid="dir-search" /></div>
      {!list ? <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
        : <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="personnel-card-grid">{rows.map((p) => (
          <PersonnelDirectoryCard key={p.id} p={p} token={token} onOpen={setSel} />))}</div>}
    </div>
  );
};

const ApplicationForms = () => {
  const { token } = useContext(AuthContext);
  const [list, setList] = useState(null); const [q, setQ] = useState(''); const [sel, setSel] = useState(null);
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/personnel?status_filter=applications`, auth(token)),
      axios.get(`${API}/personnel?status_filter=all`, auth(token)),
    ]).then(([apps, active]) => {
      const norm = (r) => (Array.isArray(r.data) ? r.data : (r.data.personnel || r.data.items || []));
      setList([...norm(apps), ...norm(active)]);
    }).catch(() => setList([]));
  }, [token]);
  if (sel) return (
    <div data-testid="application-forms">
      <button onClick={() => setSel(null)} className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-[#1763B6]"><ArrowLeft size={15} /> Back to list</button>
      <div className="mb-3 text-lg font-extrabold text-[#0E2A47]">{sel.name} · {sel.personnel_number ? `#${sel.personnel_number}` : (sel.application_id || 'Application')}</div>
      <GovFormsPanel personnelId={sel.id} person={sel} />
    </div>
  );
  const rows = (list || []).filter((p) => !q || (p.name || '').toLowerCase().includes(q.toLowerCase()) || String(p.personnel_number).includes(q) || (p.application_id || '').toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="application-forms-picker">
      <div className="mb-3 flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2"><Search size={15} className="text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Choose a person to open their application forms" className="w-full text-sm outline-none" data-testid="forms-search" /></div>
      {!list ? <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
        : <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">{rows.map((p) => (
          <button key={p.id} onClick={() => setSel(p)} data-testid={`forms-pick-${p.personnel_number || p.application_id || p.id}`} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm hover:border-[#1763B6]"><span><span className="font-semibold text-[#0E2A47]">{p.name}</span> <span className="text-slate-400">{p.personnel_number ? `#${p.personnel_number}` : (p.application_id || 'Application')}</span><div className="text-[11px] text-slate-500">{p.classification || p.worker_classification || '—'}</div></span><FileText size={15} className="text-[#1763B6]" /></button>))}</div>}
    </div>
  );
};

const Applications = () => {
  const { token } = useContext(AuthContext);
  const [list, setList] = useState(null); const [sel, setSel] = useState(null);
  const load = () => axios.get(`${API}/personnel?status_filter=applications`, auth(token)).then((r) => setList(Array.isArray(r.data) ? r.data : (r.data.personnel || []))).catch(() => setList([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);
  if (sel) return <EmployeeProfileCard personnelId={sel} onClose={() => { setSel(null); load(); }} />;
  const tone = { Draft: 'text-slate-600 bg-slate-100', 'Pending Approval': 'text-amber-700 bg-amber-50', 'Returned for Correction': 'text-amber-700 bg-amber-50', Rejected: 'text-rose-600 bg-rose-50' };
  return (
    <div data-testid="applications-list">
      <p className="mb-3 text-xs text-slate-500">Draft and in-review applications. These are not active personnel and have no permanent number until the Global Administrator approves them.</p>
      {!list ? <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
        : list.length === 0 ? <p className="py-10 text-center text-sm text-slate-400">No draft or pending applications.</p>
        : <div className="space-y-2">{list.map((p) => (
          <button key={p.id} onClick={() => setSel(p.id)} className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left hover:border-[#1763B6]" data-testid={`application-row-${p.id}`}>
            <span><span className="font-semibold text-[#0E2A47]">{p.name || 'Unnamed applicant'}</span> <span className="text-xs text-slate-400">{p.application_id || 'Application'} · {p.worker_classification || '—'}</span></span>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone[p.lifecycle_status] || 'bg-slate-100 text-slate-600'}`}>{p.lifecycle_status || 'Draft'} →</span></button>))}</div>}
    </div>
  );
};

const TABS = [
  { key: 'directory', label: 'Personnel Directory', icon: Users },
  { key: 'newhire', label: 'Add Personnel', icon: UserPlus },
  { key: 'applications', label: 'Applications', icon: ClipboardList },
  { key: 'forms', label: 'Application Forms', icon: FileText },
  { key: 'approvals', label: 'Hiring Approvals', icon: ClipboardCheck, gaOnly: true },
];

export default function PersonnelHub({ embedded = false }) {
  const { user } = useContext(AuthContext);
  const { token } = useContext(AuthContext);
  const [tab, setTab] = useState('directory');
  const [createdId, setCreatedId] = useState(null);
  const [showNhIntake, setShowNhIntake] = useState(false);
  const [focusId, setFocusId] = useState(null);
  const [backTo, setBackTo] = useState(null);
  const [counts, setCounts] = useState({});
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const t = p.get('tab'); const f = p.get('focus'); const fr = p.get('from');
    if (t && TABS.some((x) => x.key === t)) setTab(t);
    if (f) setFocusId(f);
    if (fr === 'admin') setBackTo({ href: '/tfs-admin', label: 'Administration Hub' });
    else if (fr === 'ops' || fr === 'operations') setBackTo({ href: '/tfs-ops', label: 'Operations Hub' });
  }, []);
  // Actionable-count badges per section, derived from the authoritative,
  // permission-filtered notification center (same source as the header bell).
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/notifications/center`, auth(token)).then((r) => {
      const items = r.data?.items || [];
      const c = {};
      items.forEach((it) => {
        const link = it.link || '';
        if (!link.includes('/personnel-hub')) return;
        if (link.includes('tab=approvals')) c.approvals = (c.approvals || 0) + 1;
        else if (link.includes('tab=applications')) c.applications = (c.applications || 0) + 1;
        else c.directory = (c.directory || 0) + 1;
      });
      setCounts(c);
    }).catch(() => {});
  }, [token, focusId, tab]);
  const clearFocus = () => { setFocusId(null); window.history.replaceState({}, '', '/personnel-hub'); };
  const tabs = TABS.filter((t) => !t.gaOnly || isGlobalAdmin(user));
  const navItems = tabs.map((t) => ({ key: t.key, label: t.label, icon: t.icon, badge: counts[t.key] || 0 }));
  const onNav = (key) => { setTab(key); setCreatedId(null); clearFocus(); };
  const backAction = backTo ? () => window.location.assign(backTo.href) : null;

  const content = (
    <>
      {focusId ? (
        <div data-testid="personnel-focus">
          <EmployeeProfileCard personnelId={focusId} onClose={clearFocus} onHiringDecision={clearFocus} />
        </div>
      ) : (<>
        {tab === 'directory' && <Directory />}
        {tab === 'applications' && <Applications />}
        {tab === 'newhire' && !createdId && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#1763B6]/30 bg-blue-50/50 p-4">
            <div className="text-sm text-slate-600">
              <div className="font-bold text-[#0E2A47]">Have a scanned employment packet?</div>
              Upload the PDF and let AI extract the fields — duplicate people are detected before a record is created.
            </div>
            <button onClick={() => setShowNhIntake(true)} data-testid="newhire-upload-packet"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#1763B6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#12518f]">
              <UserPlus size={15} /> Upload PDF &amp; Match (Add Personnel)
            </button>
          </div>
        )}
        {tab === 'newhire' && (createdId ? <EmployeeProfileCard personnelId={createdId} onClose={() => { setCreatedId(null); setTab('directory'); }} /> : <NewHire onCreated={(id) => setCreatedId(id)} />)}
        {tab === 'forms' && <ApplicationForms />}
        {tab === 'approvals' && isGlobalAdmin(user) && <Approvals />}
      </>)}
      {showNhIntake && <PersonnelIntake newHire personnelName="New hire" onClose={() => setShowNhIntake(false)} />}
    </>
  );

  // Embedded inside a parent hub (Administration / Operations) that already
  // provides the application shell — render content with a simple tab bar only,
  // so we never create a second header or the default work-order branding.
  if (embedded) {
    return (
      <div data-testid="personnel-hub" style={{ fontFamily: "'Century Gothic', 'Jost', sans-serif" }}>
        <div className="mb-5 flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1" data-testid="personnel-tabbar">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => onNav(t.key)}
              className={`relative inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold ${!focusId && tab === t.key ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47] hover:bg-slate-100'}`}
              data-testid={`tab-${t.key}`}><t.icon size={15} />{t.label}
              {counts[t.key] > 0 && <span data-testid={`tab-badge-${t.key}`} className="ml-0.5 rounded-full bg-[#C8641E] px-1.5 py-0.5 text-[10px] font-bold text-white">{counts[t.key]}</span>}
            </button>
          ))}
        </div>
        {content}
      </div>
    );
  }

  // Standalone route (/personnel-hub) — provide the full application shell.
  return (
    <div data-testid="personnel-hub" style={{ fontFamily: "'Century Gothic', 'Jost', sans-serif" }}>
      <SidebarShell
        navItems={navItems}
        activeKey={focusId ? null : tab}
        onNavigate={onNav}
        brandTitle="PERSONNEL"
        brandSubtitle="Management"
        workspaceLabel="PERSONNEL"
        logoutTo="/login"
        backAction={backAction}
        mobileNavGrid
        mobileNavAccent="#0E2A47"
      >
        <h1 className="mb-4 text-2xl font-extrabold text-[#0E2A47]" data-testid="personnel-title">Personnel Management</h1>
        {content}
      </SidebarShell>
    </div>
  );
}
